//#region Imports
import {
  clearOptedOutCategoryCookies,
  loadOneTrustScript,
} from "./onetrustLoader";
import { storeAndInitGTM } from "./googleBootstrap";
import type {
  AnalyticsInterface,
  Json,
  Plugin,
  PluginConfig,
} from "./generalUtils";
import {
  initializePlugins,
  waitForAnalyticsModule,
  deferredPromise,
  startCookieMonitoring,
  setupConsentUpdateDispatch,
  mockSatellite,
  loadScript,
} from "./generalUtils";

import { ONETRUST_CONSENT_COOKIE_NAME } from "./generalUtils/consts";

// I understand this feels silly but due to how barrel files worked this was getting bundled into the worker
import { AnalyticsLite } from "./generalUtils/AnalyticsLite";
import * as featureFlags from "./featureFlags";
import defaultValues from "config:./defaultValues";
import "./polyfills";
import { AnalyticsModuleBootstrapError, getDebug, logger } from "./debug";
import { getAnalyticsEventId } from "./generalUtils/getAnalyticsEventId";
import {
  federatedConfigVersionChecker,
  federate,
} from "./generalUtils/federated";
import type {
  LamModule,
  TransformationModule,
  ValidationModule,
  ExperimentModule,
} from "../@types/mwa-analytics";
import {
  TRANSFORM_MODULE_ALT_NAME,
  LAM_MODEL_FUNCTIONS_ALT_NAME,
  VALIDATION_MODULE_ALT_NAME,
  EXPERIMENT_MODULE_ALT_NAME,
  LAM_TRANSFORMATIONS_EVENT_FLAG,
} from "./generalUtils/consts";
import { setIntlOTConsentCookie } from "./generalUtils/setIntlOTConsentCookie";
import type { FilterWorkerConfig } from "./automated/types";

//#endregion

//#region Initial Configuration & Setup

/**
 * Initialize feature flag context for main thread
 * This ensures unified behavior between main thread and workers
 * Resolves which flag set (nonprod/prod) to use based on environment
 */
(function initializeFeatureFlagContext(flags: featureFlags.FeatureFlagsConfig) {
  try {
    let id = localStorage.getItem("_ambfid");
    if (!id) {
      id = window.crypto.randomUUID();
      localStorage.setItem("_ambfid", id);
    }
    featureFlags.setContext(id, flags);
    if (getDebug()) {
      logger.debug("Feature flag context initialized", {
        id: featureFlags.getId(),
        flags: featureFlags.getFlags(),
      });
      const flagResults = Object.entries(flags).reduce((acc, [flag, value]) => {
        acc[flag] = featureFlags.isEnabled(flag);
        return acc;
      }, {});
      logger.debug("Resolved feature flags", flagResults);
    }
  } catch (error) {
    logger.error(
      new AnalyticsModuleBootstrapError(
        "Failed to initialize feature flag context",
        { cause: error }
      )
    );
  }

  window["__analytics_ff"] = featureFlags;
})(defaultValues?.featureFlags || {});

// OneTrust Loader - Prevent double loading
if (
  defaultValues &&
  typeof window !== "undefined" &&
  !document?.getElementById("lll-onetrust-script")
) {
  // KR-only cookie reset; safe to remove when this OptanonConsent override is no longer needed.
  if (window.location.hostname === "www.lululemon.co.kr") {
    const expiredCookieDate = "Thu, 01 Jan 1970 00:00:00 GMT";
    document.cookie = `${ONETRUST_CONSENT_COOKIE_NAME}=; Expires=${expiredCookieDate}; Max-Age=0; Path=/; Domain=.lululemon.co.kr; SameSite=Lax`;
    document.cookie = `${ONETRUST_CONSENT_COOKIE_NAME}=; Expires=${expiredCookieDate}; Max-Age=0; Path=/; SameSite=Lax`;
  }

  if (!defaultValues.oneTrustId) {
    console.warn(
      "OneTrust domain script ID is required. Skipping script load."
    );
  } else {
    loadOneTrustScript(defaultValues.oneTrustId, defaultValues.dataLayerName)
      .then(() => {
        logger.debug("OneTrust script loaded");
        setIntlOTConsentCookie();
        clearOptedOutCategoryCookies();

        // Set up listener for consent changes
        const existingWrapper = window.OptanonWrapper;
        window.OptanonWrapper = function () {
          if (existingWrapper) {
            existingWrapper.apply(this, arguments);
          }
          setIntlOTConsentCookie();
          clearOptedOutCategoryCookies();
        };
      })
      .catch((error) => {
        logger.error(
          new AnalyticsModuleBootstrapError("Failed to load OneTrust script", {
            cause: error,
          })
        );
      });
  }
}

// Start cookie monitoring for consent management
if (defaultValues?.consentCookie) {
  startCookieMonitoring(
    (defaultValues?.plugins || []) as PluginConfig,
    defaultValues.consentCookie || ONETRUST_CONSENT_COOKIE_NAME
  );
}

if (
  typeof document !== "undefined" &&
  defaultValues?.plugins?.["google-ads"] &&
  featureFlags.isEnabled("google-ads-gtg") === true
) {
  console.log("Google Ads feature flag status | ", window?.["__analytics_ff"]?.isEnabled("google-ads-gtg"));
  // Your code to handle google-ads plugin initialization goes here
  defaultValues.plugins["google-ads"].enabled = true;
}

// Initialize plugins
const pluginsToUse =
  defaultValues["plugins"] !== undefined
    ? (initializePlugins(
        defaultValues["plugins"],
        defaultValues,
        new URLSearchParams(window.location.search)
      ) as unknown as Json[])
    : [];

try {
  mockSatellite(defaultValues.dataLayerName ?? "dataLayer");
} catch (error) {
  logger.error(
    new AnalyticsModuleBootstrapError("Failed to mock satellite", {
      cause: error,
    })
  );
}

if (
  typeof document !== "undefined" &&
  defaultValues?.adobeUrl &&
  featureFlags.isEnabled("satellite-freeze") === false
) {
  const adobeScriptSelector = "script[src*='assets.adobedtm.com']";

  if (getDebug()) {
    // Don't bother doing this in non-debug mode since it requires a DOM query
    logger.debug("Loading Adobe script:", defaultValues.adobeUrl);
    logger.debug(
      "Existing Adobe scripts:",
      document.querySelectorAll(adobeScriptSelector)
    );
  }

  const hasAdobeScript = document.querySelector(adobeScriptSelector) !== null;
  if (!hasAdobeScript) {
    loadScript(defaultValues.adobeUrl)
      .promise.then(() => {
        logger.debug("Adobe script loaded successfully");
      })
      .catch((error) => {
        logger.error(
          new AnalyticsModuleBootstrapError("Failed to load Adobe script", {
            cause: error,
          })
        );
      });
  } else {
    logger.debug("Adobe script already present, skipping load");
  }
}

//#endregion

//#region Type Definitions

type Modules =
  | "validationConfig"
  | "experimentModule"
  | "transformationModule"
  | "lamModel"
  | "metarouterPlugin";

declare global {
  interface Window {
    // Don't want to bring in next just for this one type...
    __NEXT_DATA__: {
      props: Record<string, any>;
    };
    __NEXT_REDUX_STORE__: {
      getState: () => any;
    };
    __app_config__: any; // Can be used to recognize if we're in V3
    analyticsFederatedHost?: string;
    analyticsFederatedPath?: string;
    analyticsModuleVersion?: string;
    analyticsTransformations?: Json;
    analyticsPlugins?: Json[];
    simulatedPlugins?: Json[];
    analyticsDebug?: string | boolean | string[];
    analyticsName?: string;
    analyticsFederatedModuleVersions?: {
      [K in Modules]?: string;
    };
    analytics?: AnalyticsInterface;
    eventMap?: Map<string, [string, Set<string>]>;
    lllAnalyticsSessionId?: number;
    OptanonWrapper?: () => void;
    OnetrustActiveGroups?: string;
  }
}

//#endregion

//#region Helper Functions - Script Loading & Module Initialization

function initModuleIfNeeded(
  windowName: string,
  moduleWindowName: string,
  moduleNameToImport?: string
) {
  if (typeof window === undefined) {
    return Promise.reject("Pleasure ensure this only runs in the window!");
  }

  if (window[moduleWindowName] === undefined) {
    return window[windowName]
      .get(moduleNameToImport ?? `./${windowName}`)
      .then((containerMod) => containerMod())
      .then((remoteMod) => {
        window[moduleWindowName] = remoteMod;
      });
  } else {
    return Promise.resolve(window[moduleWindowName]);
  }
}

function preloadScript(url: string) {
  const link = document.createElement("link");
  link.href = url;
  link.as = "script";
  link.rel = "preload";
  document.head.append(link);
}

function importScript(
  url: string,
  options?: {
    type?: string;
    windowName?: string;
    preload?: boolean;
  }
) {
  if (options?.windowName && window[options.windowName] !== undefined) {
    return Promise.resolve();
  }

  if (options?.preload) {
    preloadScript(url);
    return Promise.resolve();
  }

  return new Promise<void>((res, rej) => {
    const script = document.createElement("script");
    script.src = url;
    script.type = options?.type ?? "text/javascript";
    script.async = true;

    script.onload = () => {
      document.head.removeChild(script);
      res();
    };
    script.onerror = (err) => {
      document.head.removeChild(script);
      rej(err);
    };

    document.head.appendChild(script);
  });
}

//#endregion

//#region Federated Module System

// Configuration for federated modules
const defaultHost =
  window.location.host === "stage.lululemon.com"
    ? window.location.host
    : "shop.lululemon.com";
const host = window.analyticsFederatedHost || defaultHost;
const path = window.analyticsFederatedPath || "/static/fmodules/dcp/";
const versionOverrides = window.analyticsFederatedModuleVersions;
const modules = {
  "validation-configs": {
    version:
      (defaultValues && defaultValues.validationConfig) ||
      (versionOverrides && versionOverrides.validationConfig) ||
      "latest",
    windowName: "validationConfigs",
    moduleWindowName: "validationModule",
  },
  "experiment-module": {
    version:
      (defaultValues && defaultValues.experimentModule) ||
      (versionOverrides && versionOverrides.experimentModule) ||
      "latest",
    windowName: "experimentModule",
    moduleWindowName: "experimentalFunctions",
  },
  "transformation-configs": {
    version:
      (defaultValues && defaultValues.transformationModule) ||
      (versionOverrides && versionOverrides.transformationModule) ||
      "latest",
    windowName: "transformationConfigs",
    moduleWindowName: "transformationModule",
  },
  "lam-model": {
    version:
      (defaultValues && defaultValues.lamModel) ||
      (versionOverrides && versionOverrides.lamModel) ||
      "latest",
    windowName: "lamModel",
    moduleWindowName: "lamModelFunctions",
  },
};

const federatedModulesInstall = Object.keys(modules)
  .map((federatedModule) => [
    `https://${host}${path}${federatedModule}/${modules[federatedModule].version}/${federatedModule}-remoteEntry.js`,
    modules[federatedModule].windowName,
    modules[federatedModule].moduleWindowName,
    modules[federatedModule].moduleNameToImport,
  ])
  .map(([federatedModuleUrl, windowName, moduleWindowName, moduleToImport]) =>
    importScript(federatedModuleUrl, { windowName }).then(() =>
      initModuleIfNeeded(windowName, moduleWindowName, moduleToImport)
    )
  );

if (defaultValues?.platformFederatedModules) {
  defaultValues
    .platformFederatedModules(host, path, versionOverrides)
    .forEach(([federatedModuleUrl, options]) =>
      federatedModulesInstall.push(importScript(federatedModuleUrl, options))
    );
}

const analyticsModuleVersion =
  (defaultValues && defaultValues.analyticsModuleVersion) ||
  window.analyticsModuleVersion ||
  "latest";

const mwaAnalyticsUrl = `https://${host}${path}@lululemon/mwa-analytics/${analyticsModuleVersion}/browser/mwa-analytics.js`;
// This will start loading the analytics module in case you need it but not unnecssarily load it!
importScript(mwaAnalyticsUrl, {
  windowName: "MwaAnalytics",
  preload: true,
});

const analyticsModuleSimulatedReady = deferredPromise();
const instanceName = window.analyticsName || "bootstrapped";

// Load federated analytics modules
async function loadFederatedAnalyticsModules() {
  const versionChecker = federatedConfigVersionChecker();

  return Promise.all([
    federate<LamModule>("lamModel", "./lamModel", (remote) => {
      // versionChecker.majorCheck("lamModel", remote.MODULE_VERSION as string);
      self[LAM_MODEL_FUNCTIONS_ALT_NAME] = remote;
    }).then(() =>
      federate<TransformationModule>(
        "transformationConfigs",
        "./transformationConfigs",
        (remote) => {
          versionChecker.majorCheck(
            "transformationConfigs",
            remote.MODULE_VERSION as string
          );
          self[TRANSFORM_MODULE_ALT_NAME] = remote;
        }
      )
    ),
    federate<ValidationModule>(
      "validationConfigs",
      "./validationConfigs",
      (remote) => {
        self[VALIDATION_MODULE_ALT_NAME] = remote;
        versionChecker.majorCheck(
          "validationConfigs",
          remote.MODULE_VERSION as string
        );
      }
    ),
    federate<ExperimentModule>(
      "experimentModule",
      "./experimentModule",
      (remote) => {
        self[EXPERIMENT_MODULE_ALT_NAME] = remote;
        versionChecker.majorCheck(
          "experimentModule",
          remote.MODULE_VERSION as string
        );
      }
    ),
  ])
    .then(() => {
      versionChecker.minorCheck();
    })
    .catch((err) => {
      logger.error(err);
    })
    .finally(() => {
      window[LAM_TRANSFORMATIONS_EVENT_FLAG] = true;
    });
}

//#endregion

//#region Analytics Instance Setup

// Initialize the analytics module
async function initializeAnalyticsModule() {
  if (defaultValues.injectBefore) {
    defaultValues.injectBefore();
  }

  if (window.analytics === undefined) {
    if (
      (window.location.host !== "stage.lululemon.com" &&
        window.location.host !== "shop.lululemon.com") ||
      (defaultValues?.isMwaPackaged?.() ?? false) === true
    ) {
      return Promise.all(federatedModulesInstall)
        .then(() =>
          importScript(mwaAnalyticsUrl, {
            windowName: "MwaAnalytics",
          })
        )
        .then(() => {
          if (window.MwaAnalytics === undefined) {
            return Promise.reject(
              "[Analytics Module Bootstrap] There was an issue with install the Analytics Module. Please check your errors and network tab"
            );
          }
          return window.MwaAnalytics?.initializeAnalytics(
            instanceName,
            window.analyticsTransformations || {},
            pluginsToUse,
            getDebug()
          ) as unknown as Promise<(typeof window)["analytics"]>;
        });
    } else {
      return waitForAnalyticsModule();
    }
  }

  return Promise.resolve(window.analytics as AnalyticsInterface);
}

function injectMethodsToAnalyticsModule(instance: AnalyticsInterface) {
  // Inject method onto analytics module
  instance.getAnalyticsEventId = getAnalyticsEventId;

  instance["errorFnReady"] = 
    import("./automated/sendToAnalytics").then((module) =>
      import("config:./filterWorkerConfig").then((fil) => {
        // Leaving this until it is more permanent
        instance["sendLam2Error"] = 
        // @ts-ignore
          (data: any) => module.sendToAnalyticsModule({
                data: {
                  eventName: "error-api",
                  eventType: "passive",
                  eventSubType: "api-response",
                  inputData: data,
                  options: {
                    writeWithoutValidation: false
                  }
                }
              }, fil.default);
      })).catch(logger.error);
  return Promise.resolve(instance);
}

// Setup session ID using BroadcastChannel for cross-tab synchronization
async function setupSessionId(instance: AnalyticsInterface) {
  const channel = new BroadcastChannel("analytics-engine-session-id-channel");
  const setSessionId = (sessionId: string) => {
    instance.analytics.storage.setItem("sessionId", sessionId, {
      storage: "sessionStorage",
    });
    instance.analytics.identify(
      (instance.analytics.user("userId") as string) || "",
      {
        sessionId,
      }
    );
  };
  const currentSessionId = instance.analytics.user(
    "traits.sessionId"
  ) as string;

  if (!currentSessionId) {
    const hydrationPromise = new Promise<void>((res) => {
      function handler(event) {
        if (event.data.type === "setSessionId") {
          channel.removeEventListener("message", handler);
          setSessionId(event.data.sessionId);
          res();
        }
      }
      channel.addEventListener("message", handler);
      channel.postMessage({ type: "getSessionId" });
      setTimeout(() => {
        // Generate new session id
        channel.removeEventListener("message", handler);
        const newSessionId = window.crypto.randomUUID();
        setSessionId(newSessionId);
        res();
      }, 0);
    });
    await hydrationPromise;
  } else {
    channel.onmessage = (event) => {
      if (event.data.type === "getSessionId") {
        channel.postMessage({
          type: "setSessionId",
          sessionId: currentSessionId,
        });
      }
    };
  }
  return instance;
}

// Initialize simulated plugins for cases where analytics module loaded after plugins
async function initializeSimulatedPlugins(instance: AnalyticsInterface) {
  // If this is true we can safely assume we missed the cut
  // However, don't fret we can simulate the behaviour!
  if (instance?.options?.name !== instanceName) {
    instance.analytics.on("disablePlugin", ({ payload }) => {
      payload.plugins.forEach((plugin) => {
        AnalyticsLite.disablePlugin(plugin);
      });
    });
    instance.analytics.on("simulatedPluginEnabled", ({ payload }) => {
      payload.plugins.forEach((plugin: string) => {
        AnalyticsLite.enablePlugin(plugin);
      });
    });
    window.simulatedPlugins = pluginsToUse;
    return Promise.all(
      pluginsToUse.map((plugin) => {
        if (plugin?.["config"]?.simulate) {
          AnalyticsLite.addPlugin(plugin as unknown as Plugin).catch(
            logger.error
          );
        } else {
          instance["addPlugin"](plugin).catch(logger.error);
        }
      })
    ).then(() => {
      logger.debug("setup done");
      analyticsModuleSimulatedReady.res();
    });
  } else {
    analyticsModuleSimulatedReady.res();
  }
}

//#endregion

//#region Bootstrap & Execution

// Start GTM and Analytics Module in parallel
Promise.all([
  storeAndInitGTM().catch(logger.error),
  loadFederatedAnalyticsModules()
    .then(initializeAnalyticsModule)
    .then(setupSessionId)
    .then((res) => {
      window.lllAnalyticsReadyEventFlag = true;
      if (featureFlags.isEnabled("satellite-freeze")) {
        document.dispatchEvent(new CustomEvent("launch-library-loaded"));
        window._satellite.track = () => {};
      }
      return res;
    })
    .then(injectMethodsToAnalyticsModule)
    .then(initializeSimulatedPlugins)
    .then(setupConsentUpdateDispatch)
    .catch(logger.error),
])
  .then(() => {
    console.log("Analytics Module Successfully started");
  })
  .catch((e) => logger.error(e));

// Debug utilities
window["_analyticsLite"] = AnalyticsLite;

// Enable automated tracking if configured
if (defaultValues?.automatedEnabled === true) {
  // Force code splitting to avoid loading automated tracking in all apps
  import("./automated/index").then((mod) => {
    mod.startInterceptorAndWorker(analyticsModuleSimulatedReady.promise);
  });
}

// For INTL compatibility
window["fetchWrapper"] = fetch;

//#endregion
